// Consent Manager for UsedFindr
// Centralized privacy-compliant consent management system
// Handles both Google Analytics (GA4) and internal AnalyticsManager

// Initialize logger for this module
const logger = window.Logger ? window.Logger.create('consent') : {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: console.error.bind(console)
};

/**
 * Returns true if the current page is the admin console (or admin login).
 * GA and analytics tracking are disabled on admin for privacy and data quality.
 */
function isAdminConsole() {
  if (typeof window === 'undefined' || !window.location) return false;
  return (window.location.pathname || '').includes('/admin');
}

export class ConsentManager {
  constructor() {
    this.storageKey = 'usedfindr_consent_state';
    this.legacyKey = 'usedfindr_cookie_consent'; // For migration
    this.measurementId = window.USEDFINDR_CONFIG?.ga4MeasurementId || 'G-88NLFEXCZY';
    this.listeners = {};
    
    // Initialize GA4 Consent Mode BEFORE anything else
    this.initializeConsentMode();
    
    // Migrate legacy consent if needed
    this.migrateLegacyConsent();
    
    // Load or create consent state
    this.consentState = this.getConsentState();
    
    // Apply current consent state
    if (this.consentState) {
      this.applyConsent(this.consentState);
    }
    
    logger.info('ConsentManager initialized', this.consentState);
  }
  
  /**
   * Initialize GA4 Consent Mode v2 with default denied state
   * This MUST run before GA4 loads to be compliant
   */
  initializeConsentMode() {
    // Create dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];
    
    // Create gtag function if it doesn't exist
    if (!window.gtag) {
      function gtag(){window.dataLayer.push(arguments);}
      window.gtag = gtag;
    }
    
    // Set default consent to DENIED (GDPR-compliant opt-in)
    window.gtag('consent', 'default', {
      'ad_storage': 'denied',
      'analytics_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'functionality_storage': 'denied',
      'personalization_storage': 'denied',
      'security_storage': 'granted', // Always granted for CSRF tokens, etc.
      'wait_for_update': 500 // Wait 500ms for consent before tracking
    });
    
    logger.info('GA4 Consent Mode initialized with default: denied');
  }
  
  /**
   * Migrate from legacy consent storage format
   */
  migrateLegacyConsent() {
    try {
      const legacy = localStorage.getItem(this.legacyKey);
      if (legacy && !localStorage.getItem(this.storageKey)) {
        const legacyData = JSON.parse(legacy);
        const migratedState = {
          necessary: true,
          analytics: legacyData.analytics || false,
          marketing: legacyData.marketing || false,
          preferences: legacyData.preferences || false,
          timestamp: legacyData.timestamp || new Date().toISOString(),
          version: '2.0'
        };
        
        localStorage.setItem(this.storageKey, JSON.stringify(migratedState));
        localStorage.removeItem(this.legacyKey);
        logger.info('Migrated legacy consent data');
      }
    } catch (error) {
      logger.error('Error migrating legacy consent:', error);
    }
  }
  
  /**
   * Get current consent state from localStorage
   * @returns {Object|null} Consent state or null if not set
   */
  getConsentState() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved) {
        const state = JSON.parse(saved);
        // Ensure necessary is always true
        state.necessary = true;
        return state;
      }
      return null;
    } catch (error) {
      logger.error('Error reading consent state:', error);
      return null;
    }
  }
  
  /**
   * Save consent state to localStorage
   * @param {Object} state - Consent state object
   */
  saveConsentState(state) {
    try {
      const stateToSave = {
        ...state,
        necessary: true, // Always true
        timestamp: new Date().toISOString(),
        version: '2.0'
      };
      
      localStorage.setItem(this.storageKey, JSON.stringify(stateToSave));
      this.consentState = stateToSave;
      logger.info('Consent state saved:', stateToSave);
    } catch (error) {
      logger.error('Error saving consent state:', error);
    }
  }
  
  /**
   * Check if user has given consent for a specific type
   * @param {string} type - Consent type: 'analytics', 'marketing', 'preferences', 'necessary'
   * @returns {boolean} True if consent granted
   */
  hasConsent(type) {
    // Necessary cookies are always granted
    if (type === 'necessary') return true;
    
    // Disable analytics (and marketing/preferences for third-party scripts) on admin console
    if (isAdminConsole() && (type === 'analytics' || type === 'marketing')) {
      return false;
    }
    
    const state = this.consentState || this.getConsentState();
    if (!state) {
      // No consent given yet - default to denied (opt-in model)
      return false;
    }
    
    return state[type] === true;
  }
  
  /**
   * Accept all consent categories
   */
  acceptAll() {
    const newState = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    this.saveConsentState(newState);
    this.applyConsent(newState);
    this.emitEvent('consent-changed', newState);
    
    logger.info('User accepted all consent');
  }
  
  /**
   * Decline all non-necessary consent categories
   */
  declineAll() {
    const newState = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    
    this.saveConsentState(newState);
    this.applyConsent(newState);
    
    // Always purge analytics keys when user declines. This also cleans up
    // legacy data written before stricter consent gating existed.
    if (!newState.analytics) {
      this.purgeAnalyticsData();
    }
    
    this.emitEvent('consent-changed', newState);
    
    logger.info('User declined all consent');
  }
  
  /**
   * Update specific consent preferences
   * @param {Object} preferences - Object with consent preferences
   */
  updatePreferences(preferences) {
    const oldState = this.consentState || {};

    const newState = {
      necessary: true,
      analytics: preferences.analytics !== undefined ? preferences.analytics : oldState.analytics || false,
      marketing: preferences.marketing !== undefined ? preferences.marketing : oldState.marketing || false,
      preferences: preferences.preferences !== undefined ? preferences.preferences : oldState.preferences || false
    };
    
    this.saveConsentState(newState);
    this.applyConsent(newState);
    
    // Purge analytics data whenever analytics is disabled. This prevents
    // leftover local keys from older builds from lingering in storage.
    if (!newState.analytics) {
      this.purgeAnalyticsData();
    }
    
    this.emitEvent('consent-changed', newState);
    
    logger.info('User updated preferences:', newState);
  }
  
  /**
   * Apply consent state to all tracking systems
   * @param {Object} state - Consent state to apply
   */
  applyConsent(state) {
    // Update GA4 Consent Mode
    this.updateGAConsentMode(state);
    
    // Load GA4 only when analytics consent is given and we're not on the admin console
    if (state.analytics && !isAdminConsole()) {
      this.loadGoogleAnalytics();
    }
    
    // Note: AnalyticsManager gating is handled by the manager itself
    // via hasConsent() checks before each tracking call
  }
  
  /**
   * Update Google Analytics Consent Mode
   * @param {Object} state - Consent state
   */
  updateGAConsentMode(state) {
    if (!window.gtag) return;
    
    window.gtag('consent', 'update', {
      'analytics_storage': state.analytics ? 'granted' : 'denied',
      'ad_storage': state.marketing ? 'granted' : 'denied',
      'ad_user_data': state.marketing ? 'granted' : 'denied',
      'ad_personalization': state.marketing ? 'granted' : 'denied',
      'functionality_storage': state.preferences ? 'granted' : 'denied',
      'personalization_storage': state.preferences ? 'granted' : 'denied'
    });
    
    logger.info('GA4 Consent Mode updated:', {
      analytics_storage: state.analytics ? 'granted' : 'denied',
      ad_storage: state.marketing ? 'granted' : 'denied'
    });
  }
  
  /**
   * Load Google Analytics 4 script
   */
  loadGoogleAnalytics() {
    // Never load GA on the admin console (privacy + analytics data quality)
    if (isAdminConsole()) {
      logger.debug('GA4 disabled on admin console');
      return;
    }
    
    // Check if already loaded
    if (this.gaLoaded || document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
      logger.debug('GA4 already loaded');
      return;
    }
    
    // Validate measurement ID
    if (!this.measurementId || this.measurementId === 'G-XXXXXXXXXX') {
      logger.warn('GA4 Measurement ID not configured - skipping load');
      return;
    }
    
    logger.info('Loading GA4 with ID:', this.measurementId);
    
    // Create and insert GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    script.onerror = () => {
      logger.error('Failed to load GA4 script');
    };
    script.onload = () => {
      logger.info('GA4 script loaded successfully');
      this.gaLoaded = true;
      
      // Initialize GA4
      window.gtag('js', new Date());
      window.gtag('config', this.measurementId, {
        'anonymize_ip': true,
        'cookie_flags': 'SameSite=None;Secure',
        'cookie_domain': 'auto',
        'cookie_expires': 63072000 // 2 years in seconds
      });
      
      logger.info('GA4 initialized with config');
    };
    
    document.head.appendChild(script);
  }
  
  /**
   * Purge all analytics data when consent is revoked
   */
  purgeAnalyticsData() {
    logger.info('Purging analytics data due to consent withdrawal');
    
    // Clear UsedFindr AnalyticsManager data
    if (window.analyticsManager && typeof window.analyticsManager.clearAllData === 'function') {
      window.analyticsManager.clearAllData();
    }

    // Clear consent-gated behavior keys managed outside AnalyticsManager.
    try {
      localStorage.removeItem('usedfindr_product_clicks');
      localStorage.removeItem('usedfindr_recent_views');
    } catch (error) {
      logger.error('Failed clearing consent-gated localStorage keys:', error);
    }
    
    // Clear Google Analytics cookies
    this.clearGACookies();
  }
  
  /**
   * Clear Google Analytics cookies
   */
  clearGACookies() {
    const domain = window.location.hostname;
    const cookiesToClear = [
      '_ga',
      '_gid',
      '_gat',
      `_gat_gtag_${this.measurementId.replace(/-/g, '_')}`,
      '_gcl_au',
      '_gcl_aw',
      '_gcl_dc'
    ];
    
    cookiesToClear.forEach(cookieName => {
      // Clear for current domain
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      // Clear for root domain
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      // Clear for subdomain
      const rootDomain = domain.split('.').slice(-2).join('.');
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${rootDomain}`;
    });
    
    logger.info('GA cookies cleared');
  }
  
  /**
   * Event system for consent changes
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }
  
  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function to remove
   */
  off(event, callback) {
    if (!this.listeners[event]) return;
    
    this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
  }
  
  /**
   * Emit event to all listeners
   * @param {string} event - Event name
   * @param {*} data - Data to pass to listeners
   */
  emitEvent(event, data) {
    if (!this.listeners[event]) return;
    
    this.listeners[event].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        logger.error(`Error in event listener for ${event}:`, error);
      }
    });
  }
  
  /**
   * Get summary of current consent state for debugging
   * @returns {Object} Consent summary
   */
  getSummary() {
    const state = this.getConsentState();
    return {
      consentGiven: state !== null,
      state: state,
      hasAnalytics: this.hasConsent('analytics'),
      hasMarketing: this.hasConsent('marketing'),
      hasPreferences: this.hasConsent('preferences'),
      gaLoaded: this.gaLoaded || false,
      measurementId: this.measurementId
    };
  }
}

// Create and export global instance
const consentManager = new ConsentManager();

// Make available on window for global access
if (typeof window !== 'undefined') {
  window.consentManager = consentManager;
}

export { consentManager };
